import React, { useEffect, useRef } from 'react';
import { Product } from '../data/products';
import ProductCard from './ProductCard';
import { ProductMatch } from '../hooks/useRecommendation';
import { useLanguage } from '../context/LanguageContext';
import { useInactivityReset } from '../hooks/useRecommendation';

interface ResultsProps {
  products: Product[];
  productMatches: ProductMatch[];
  alternativeProducts: Product[];
  unmatchedCriteria: string[];
  hasPerfectMatch: boolean;
  onReset: () => void;
}

const Results: React.FC<ResultsProps> = ({ 
  products, 
  productMatches, 
  alternativeProducts, 
  unmatchedCriteria,
  hasPerfectMatch, 
  onReset 
}) => {
  const { t } = useLanguage();
  const resultsRef = useRef<HTMLDivElement>(null);

  // Enable inactivity reset on results screen
  useInactivityReset(onReset, true);

  useEffect(() => {
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="w-full max-w-6xl mx-auto" ref={resultsRef}>
      <div className="text-center mb-8">
        <h2 className="text-4xl font-bold text-felco-red mb-6">
          {hasPerfectMatch 
            ? t('results.perfect')
            : t('results.noMatch')}
        </h2>
        
        <p className="text-gray-700 text-xl max-w-3xl mx-auto mb-4">
          {hasPerfectMatch
            ? t('results.perfectMatch')
            : t('results.noMatchText')}
        </p>
        
        <button
          onClick={onReset}
          className="bg-felco-red hover:bg-felco-dark-red text-white font-medium py-3 px-8 rounded-lg transition-all hover:shadow-lg"
        >
          {t('btn.reset')}
        </button>
      </div>
      
      {/* Only show perfect matches */}
      {hasPerfectMatch && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {products.map((product, index) => (
            <ProductCard 
              key={product.id} 
              product={product} 
              priority={index < 3 ? 'high' : 'low'} // First 3 products load eagerly
            />
          ))}
        </div>
      )}
      
      {/* Only show alternatives when there are no perfect matches */}
      {!hasPerfectMatch && alternativeProducts.length > 0 && (
        <div className="mt-8 mb-8">
          <h3 className="text-3xl font-bold text-felco-red mb-6 text-center">{t('results.alternative')}</h3>
          <p className="text-gray-700 text-lg max-w-3xl mx-auto mb-8 text-center">
            {t('results.partialMatch')}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {alternativeProducts.map((product, index) => (
              <ProductCard 
                key={product.id} 
                product={product} 
                priority={index < 3 ? 'high' : 'low'} // First 3 products load eagerly
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Results; 